/**
 * FERP Portal - Spatial Renderer
 *
 * DRR-178 Phase 5 + DRR-179: Two rendering modes:
 *   1. Auto-grid (default): Groups items by location as a responsive card grid.
 *      Used when no coordinates are available.
 *   2. Coordinate-positioned: Nodes render at absolute positions on a floor plan.
 *      Used when LOCATED_AT edges have {x, y} metadata (DRR-179 Phase 1).
 *      Optional floor plan image as background (DRR-179 Phase 2).
 *
 * DRY across verticals: ALF rooms, construction zones, legal offices, warehouse aisles.
 * Uses CSS positioning, NOT Cytoscape (DRR-178 D7).
 */

import { useMemo, useState, useRef } from 'react';
import {
  SimpleGrid,
  Card,
  Text,
  Badge,
  Group,
  Stack,
  Paper,
  Collapse,
  UnstyledButton,
  ThemeIcon,
  ScrollArea,
  Box,
  SegmentedControl,
  Image,
  Tooltip,
} from '@mantine/core';
import { IconMapPin, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

const PRIORITY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'blue',
};

interface SpatialNodeData {
  name: string;
  subtitle?: string;
  safetyLevel?: string;
}

/** DRR-179: Coordinate data for positioned rendering */
export interface SpatialCoordinate {
  /** Location key (matches the item's location field value) */
  location: string;
  /** Normalized X (0.0–1.0), null = auto-grid fallback */
  x: number | null;
  /** Normalized Y (0.0–1.0), null = auto-grid fallback */
  y: number | null;
}

interface SpatialRendererProps<T> {
  items: T[];
  /** Field to extract location from each item (supports dot notation) */
  locationField: string;
  /** Maps an item to display fields */
  fieldAdapter: (item: T) => SpatialNodeData;
  /** Callback when an item within a node is clicked */
  onItemClick?: (item: T) => void;
  /** Extract unique ID from item */
  getId?: (item: T) => string;
  /** Number of grid columns for auto-grid mode (default: auto) */
  cols?: number;
  /** Max height for the container */
  maxHeight?: string;
  /** DRR-179: Coordinate positions for each location. When provided with valid x/y,
   *  switches from auto-grid to positioned rendering. */
  coordinates?: SpatialCoordinate[];
  /** DRR-179: Floor plan image URL for background overlay */
  floorPlanUrl?: string;
  /** DRR-179: Available floors for multi-floor selector */
  floors?: Array<{ floor: number; label: string }>;
  /** DRR-179: Currently selected floor */
  selectedFloor?: number;
  /** DRR-179: Callback when floor is changed */
  onFloorChange?: (floor: number) => void;
}

/** Extract a nested field value using dot notation */
function getNestedField(item: Record<string, unknown>, field: string): string {
  const parts = field.split('.');
  let current: unknown = item;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return '(unknown)';
    current = (current as Record<string, unknown>)[part];
  }
  return current != null ? String(current) : '(unknown)';
}

export function SpatialRenderer<T>({
  items,
  locationField,
  fieldAdapter,
  onItemClick,
  getId = (item: T) => (item as Record<string, unknown>).id as string,
  cols,
  maxHeight = '600px',
  coordinates,
  floorPlanUrl,
  floors,
  selectedFloor,
  onFloorChange,
}: SpatialRendererProps<T>) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Build coordinate lookup
  const coordMap = useMemo(() => {
    if (!coordinates) return null;
    const map = new Map<string, { x: number; y: number }>();
    for (const coord of coordinates) {
      if (coord.x != null && coord.y != null) {
        map.set(coord.location, { x: coord.x, y: coord.y });
      }
    }
    return map.size > 0 ? map : null;
  }, [coordinates]);

  const usePositionedMode = coordMap !== null;

  // Group items by location
  const nodes = useMemo(() => {
    const grouped = new Map<string, T[]>();

    for (const item of items) {
      const location = getNestedField(item as Record<string, unknown>, locationField);
      if (!grouped.has(location)) {
        grouped.set(location, []);
      }
      grouped.get(location)!.push(item);
    }

    return Array.from(grouped.entries())
      .map(([location, nodeItems]) => {
        const maxPriority = nodeItems.reduce((max, item) => {
          const level = fieldAdapter(item).safetyLevel ?? 'LOW';
          const order = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
          return (order[level as keyof typeof order] ?? 0) > (order[max as keyof typeof order] ?? 0) ? level : max;
        }, 'LOW');

        return {
          location,
          items: nodeItems,
          maxPriority,
          coord: coordMap?.get(location) ?? null,
        };
      })
      .sort((a, b) => a.location.localeCompare(b.location));
  }, [items, locationField, fieldAdapter, coordMap]);

  const toggleNode = (location: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(location)) {
        next.delete(location);
      } else {
        next.add(location);
      }
      return next;
    });
  };

  if (items.length === 0) {
    return (
      <Paper p="xl" ta="center" c="dimmed">
        <Text>No items to display</Text>
      </Paper>
    );
  }

  // Floor selector for multi-floor facilities
  const floorSelector = floors && floors.length > 1 && (
    <SegmentedControl
      size="xs"
      value={String(selectedFloor ?? floors[0]?.floor ?? 1)}
      onChange={(v) => onFloorChange?.(Number(v))}
      data={floors.map((f) => ({ value: String(f.floor), label: f.label }))}
      mb="sm"
    />
  );

  // =========================================================================
  // POSITIONED MODE (DRR-179): nodes at absolute coordinates on floor plan
  // =========================================================================
  if (usePositionedMode) {
    return (
      <ScrollArea style={{ maxHeight }} offsetScrollbars>
        {floorSelector}
        <Box
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: floorPlanUrl ? undefined : '3 / 2',
            minHeight: 400,
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 'var(--mantine-radius-md)',
            overflow: 'hidden',
            background: 'var(--mantine-color-gray-0)',
          }}
        >
          {/* Floor plan background image */}
          {floorPlanUrl && (
            <Image
              src={floorPlanUrl}
              alt="Floor plan"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: 0.3,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Positioned room nodes */}
          {nodes.map((node) => {
            if (!node.coord) return null;
            const priorityColor = PRIORITY_COLORS[node.maxPriority] ?? 'gray';
            const isExpanded = expandedNodes.has(node.location);

            return (
              <Box
                key={node.location}
                style={{
                  position: 'absolute',
                  left: `${node.coord.x * 100}%`,
                  top: `${node.coord.y * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  zIndex: isExpanded ? 10 : 1,
                }}
              >
                <Tooltip label={`${node.location} — ${node.items.length} tasks`} disabled={isExpanded}>
                  <Card
                    withBorder
                    padding="xs"
                    radius="md"
                    shadow={isExpanded ? 'md' : 'xs'}
                    style={{
                      borderLeft: `3px solid var(--mantine-color-${priorityColor}-5)`,
                      minWidth: isExpanded ? 200 : 100,
                      maxWidth: 260,
                      background: 'var(--mantine-color-body)',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleNode(node.location)}
                  >
                    <Group gap={4} wrap="nowrap">
                      <ThemeIcon size="xs" variant="filled" color={priorityColor} radius="xl">
                        <IconMapPin size={10} />
                      </ThemeIcon>
                      <Text size="xs" fw={600} lineClamp={1}>{node.location}</Text>
                      <Badge size="xs" variant="light" color={priorityColor} ml="auto">
                        {node.items.length}
                      </Badge>
                    </Group>

                    <Collapse in={isExpanded}>
                      <Stack gap={3} mt={4}>
                        {node.items.map((item) => {
                          const fields = fieldAdapter(item);
                          return (
                            <Paper
                              key={getId(item)}
                              p={4}
                              radius="sm"
                              withBorder
                              style={{ cursor: onItemClick ? 'pointer' : undefined }}
                              onClick={(e) => { e.stopPropagation(); onItemClick?.(item); }}
                            >
                              <Text size="xs" fw={500} lineClamp={1}>{fields.name}</Text>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Collapse>
                  </Card>
                </Tooltip>
              </Box>
            );
          })}

          {/* Nodes without coordinates → small indicators at bottom */}
          {nodes.filter((n) => !n.coord).length > 0 && (
            <Box style={{ position: 'absolute', bottom: 8, left: 8, right: 8 }}>
              <Group gap={4}>
                {nodes.filter((n) => !n.coord).map((node) => (
                  <Badge
                    key={node.location}
                    size="sm"
                    variant="light"
                    color={PRIORITY_COLORS[node.maxPriority] ?? 'gray'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleNode(node.location)}
                  >
                    {node.location}: {node.items.length}
                  </Badge>
                ))}
              </Group>
            </Box>
          )}
        </Box>
      </ScrollArea>
    );
  }

  // =========================================================================
  // AUTO-GRID MODE (DRR-178 Phase 5): fallback when no coordinates
  // =========================================================================
  const gridCols = cols ?? Math.min(nodes.length, 4);

  return (
    <ScrollArea style={{ maxHeight }} offsetScrollbars>
      {floorSelector}
      <SimpleGrid cols={{ base: 1, sm: 2, md: gridCols }} spacing="sm">
        {nodes.map((node) => {
          const isExpanded = expandedNodes.has(node.location);
          const priorityColor = PRIORITY_COLORS[node.maxPriority] ?? 'gray';

          return (
            <Card
              key={node.location}
              withBorder
              padding="sm"
              radius="md"
              style={{
                borderLeft: `4px solid var(--mantine-color-${priorityColor}-5)`,
              }}
            >
              <UnstyledButton onClick={() => toggleNode(node.location)} w="100%">
                <Group gap="xs" justify="space-between">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color={priorityColor}>
                      <IconMapPin size={14} />
                    </ThemeIcon>
                    <Text fw={600} size="sm">{node.location}</Text>
                  </Group>
                  <Group gap={4}>
                    <Badge size="sm" variant="light" color={priorityColor}>
                      {node.items.length}
                    </Badge>
                    {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                  </Group>
                </Group>
              </UnstyledButton>

              <Collapse in={isExpanded}>
                <Stack gap={4} mt="xs">
                  {node.items.map((item) => {
                    const fields = fieldAdapter(item);
                    return (
                      <Paper
                        key={getId(item)}
                        p="xs"
                        radius="sm"
                        withBorder
                        style={{ cursor: onItemClick ? 'pointer' : undefined }}
                        onClick={() => onItemClick?.(item)}
                      >
                        <Text size="xs" fw={500} lineClamp={1}>{fields.name}</Text>
                        {fields.subtitle && (
                          <Text size="xs" c="dimmed" lineClamp={1}>{fields.subtitle}</Text>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Collapse>
            </Card>
          );
        })}
      </SimpleGrid>
    </ScrollArea>
  );
}
